import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Brain, EnvelopeSimple } from '@phosphor-icons/react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

import { API_URL } from '../config';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <Brain size={40} weight="duotone" className="text-[#007AFF]" />
            <span className="font-heading font-black text-2xl tracking-tight text-white uppercase">
              Trader Reset AI
            </span>
          </Link>
        </div>

        <div className="bg-[#131313] border border-white/10 p-8" data-testid="forgot-password-form">
          {sent ? (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#007AFF]/10 flex items-center justify-center">
                <EnvelopeSimple size={32} className="text-[#007AFF]" />
              </div>
              <h1 className="font-heading font-bold text-2xl uppercase text-white mb-3">
                Check Your Email
              </h1>
              <p className="text-[#8892B0] mb-6">
                If an account exists with that email, we've sent a password reset link. Check your inbox and spam folder.
              </p>
              <Link
                to="/login"
                className="inline-block px-8 py-3 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-colors whitespace-nowrap"
                data-testid="back-to-login"
              >
                Back to Sign In
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading font-bold text-2xl uppercase text-white mb-2 text-center">
                Forgot Password
              </h1>
              <p className="text-[#8892B0] text-center mb-8">
                Enter your email and we'll send you a reset link
              </p>

              {error && (
                <div className="mb-6 p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] text-sm" data-testid="forgot-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="email" className="text-xs text-[#8892B0] uppercase tracking-wider mb-2 block">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-transparent border-white/20 text-white focus:border-[#007AFF] rounded-none p-4"
                    placeholder="trader@example.com"
                    required
                    data-testid="forgot-email"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  data-testid="forgot-submit"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>

              <p className="mt-6 text-center text-[#8892B0]">
                Remember your password?{' '}
                <Link to="/login" className="text-[#007AFF] hover:underline" data-testid="forgot-login-link">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
